import type { PropsWithChildren } from 'react'

import { Space, SubText, WithPopover } from '@procurify/ui'

import {
  useFormatPrice,
  useProcurifyIntl,
  useFormatTaxPercentage,
} from '@webapp/hooks'
import { type Currency, type OrderItemTax } from '@webapp/models'
import { TaxTypes } from '@webapp/models/enums/Tax'
import messages from './messages'
import { CurrencyConversionInfo } from '../CurrencyConversionInfo'
import { WithTaxTooltipStyled } from './styles/WithTaxTooltipStyled'

export interface IWithTaxTooltipProps {
  taxes: OrderItemTax[]
  currency?: Currency
  price?: number | string
  isDisabled?: boolean
}

export const WithTaxTooltip = ({
  taxes,
  currency,
  price,
  isDisabled,
  children,
}: PropsWithChildren<IWithTaxTooltipProps>) => {
  const intl = useProcurifyIntl()
  const formatTaxPercentage = useFormatTaxPercentage()
  const formatPrice = useFormatPrice()

  const getTaxName = (tax: OrderItemTax) => {
    if (tax.name) return tax.name

    return tax.type === TaxTypes.PERCENT
      ? intl.formatMessage(messages.customPercentLabel)
      : intl.formatMessage(messages.customAmountLabel)
  }

  const getTaxValue = (tax: OrderItemTax) => {
    return tax.type === TaxTypes.PERCENT
      ? formatTaxPercentage(Number(tax.value) / 100)
      : formatPrice(tax.value)
  }

  return (
    <WithPopover
      placement='top'
      isDisabled={isDisabled || !taxes?.length}
      content={
        <WithTaxTooltipStyled>
          <Space size='small'>
            {taxes?.map((tax, index) => (
              <Space key={index} direction='horizontal' justify='space-between'>
                <SubText textEllipsis>{getTaxName(tax)}</SubText>
                <SubText>{getTaxValue(tax)}</SubText>
              </Space>
            ))}

            {currency?.rate && price && (
              <CurrencyConversionInfo price={price} currency={currency} />
            )}
          </Space>
        </WithTaxTooltipStyled>
      }
    >
      {children}
    </WithPopover>
  )
}

WithTaxTooltip.displayName = 'WithTaxTooltip'
